/**
 * MenuSystem.ts — Menu rendering with highlight, scroll, and multi-input support.
 *
 * Renders numbered option lists with:
 * - `>` highlight indicator for arrow key / mouse position
 * - Separator lines between option groups
 * - 0) Exit/Back always at bottom
 *
 * Works with InputManager to receive selection events from all three
 * input methods (number entry, arrow keys, mouse clicks).
 *
 * Shared across ChoiceRealms titles (Ascend, Gallowcrest, Rotary Knights).
 */

import type { Terminal } from './Terminal';
import type { InputManager, InputEvent } from './InputManager';
import { theme, type ColorName } from './ColorTheme';
import { formatStatPeek } from './StatPeek';
import { QuickStatsOverlay, type QuickStatsOverlayOptions } from './QuickStatsOverlay';

// --- Menu Option Types ---

/** Skill check information for stat peek display */
export interface SkillCheckInfo {
  /** Stat abbreviation (e.g., 'GUN', 'PIL') */
  stat: string;
  /** Difficulty class for the check */
  dc: number;
  /** Player's current stat value */
  playerValue: number;
  /** Calculated modifier (optional, will be calculated if not provided) */
  modifier?: number;
}

export interface MenuOption {
  /** Display label */
  label: string;
  /** Optional color for this option */
  color?: ColorName;
  /** Optional suffix shown after the label (e.g., "[DC 14]") */
  suffix?: string;
  /** Optional suffix color */
  suffixColor?: ColorName;
  /** If true, this option is disabled (shown in gray, not selectable) */
  disabled?: boolean;
  /** Skill check info for stat peek display */
  skillCheck?: SkillCheckInfo;
  /** Optional description shown as a subtitle line below the option (khaki, indented) */
  description?: string;
}

export interface MenuSeparator {
  type: 'separator';
  /** Optional label for the separator */
  label?: string;
}

export type MenuItem = MenuOption | MenuSeparator;

export interface MenuConfig {
  /** Title shown above the menu */
  title?: string;
  /** Title color */
  titleColor?: ColorName;
  /** Show "0) Exit/Back" option */
  showExit?: boolean;
  /** Custom exit label (default: "Back") */
  exitLabel?: string;
  /** Starting row to render the menu (1-based) */
  startRow?: number;
  /** Callback when a menu option is selected (index is 0-based among selectable options) */
  onSelect?: (index: number) => void;
  /** Callback when exit/cancel is triggered */
  onCancel?: () => void;
  /** Enable quick stats overlay on Tab/? key */
  enableQuickStats?: boolean;
  /** Stats data for quick stats overlay */
  quickStatsData?: QuickStatsOverlayOptions;
  /** Enable "View Character Sheet" option */
  enableCharacterSheet?: boolean;
  /** Callback when character sheet is requested */
  onCharacterSheet?: () => Promise<void>;
  /** Clear all rows below the menu on first render (prevents narrative bleed-through) */
  clearBelow?: boolean;
  /** Number of rows to preserve at the bottom (e.g., PANEL_HEIGHT=5 for status bars) */
  reserveBottomRows?: number;
  /** Number of columns to distribute menu items across (default 1) */
  columns?: number;
  /** Starting column for the menu (1-based, default 1) */
  startCol?: number;
  /** Maximum width for the menu area (useful for panel-constrained layouts) */
  maxWidth?: number;
  /** Called before each menu render — use to redraw the frame/content above the menu on resize */
  onBeforeRender?: () => { startRow: number; startCol: number; maxWidth: number } | void;
  /** Initial highlight index (0-based). Defaults to 0. */
  initialHighlightIndex?: number;
  /** Max items visible before scrolling kicks in. Defaults to showing all items (no scroll). */
  maxVisibleItems?: number;
  /** Wrap menu items in box-drawing borders */
  boxed?: boolean;
  /** Color style for selected items: 'enhanced' uses amber prefix + cream text */
  enhancedHighlight?: boolean;
  /**
   * Called when ArrowLeft/ArrowRight is pressed in menu mode.
   * Use this to handle directional navigation (e.g., map sector selection)
   * that runs alongside the menu. Return true if the event was consumed.
   */
  onDirectionKey?: (direction: 'left' | 'right' | 'up' | 'down') => boolean;
  /**
   * Called when Enter is pressed before the menu processes it.
   * Return a resolved index (0-based) to short-circuit the menu,
   * or -2 to let the menu handle the Enter normally.
   * Return -1 to trigger exit/cancel.
   */
  onEnterOverride?: () => number;
  /**
   * Called when a hotkey is pressed (Tab, ?, c, C).
   * Use to intercept hotkeys for custom handling (e.g., Tab focus toggle).
   * Return true if the event was consumed and should not be processed by
   * the default hotkey handler (quick stats, character sheet).
   */
  onHotkey?: (key: string) => boolean;
  /**
   * Called when the highlight index changes (arrow keys, mouse hover).
   * Receives the current highlighted item index (0-based among selectable items).
   */
  onHighlight?: (index: number) => void;
}

// --- Scroll helpers (pure, exported for testing) ---

/**
 * Compute the scroll offset so that the highlighted item is always visible
 * within the scroll window [scrollOffset, scrollOffset + maxVisible).
 *
 * The highlightIdx here refers to the position in the FULL items array
 * (not the selectable-only index). The caller must map from selectable
 * highlight index to the items-array position before calling this.
 *
 * @param highlightIdx  Index of the highlighted item in the full items array
 * @param currentScrollOffset  Current scroll offset
 * @param maxVisible  Maximum number of items visible at once (clamped to ≥ 1)
 * @returns The new scroll offset
 */
export function computeScrollOffset(
  highlightIdx: number,
  currentScrollOffset: number,
  maxVisible: number,
): number {
  const mv = Math.max(1, maxVisible);
  let offset = currentScrollOffset;
  // If highlighted item is above the visible window, scroll up
  if (highlightIdx < offset) {
    offset = highlightIdx;
  }
  // If highlighted item is below the visible window, scroll down
  if (highlightIdx >= offset + mv) {
    offset = highlightIdx - mv + 1;
  }
  return Math.max(0, offset);
}

/**
 * Determine whether scroll indicators should be shown.
 *
 * @param scrollOffset  Index of the first visible item
 * @param maxVisible  Number of items visible at once
 * @param totalItems  Total number of items in the list
 * @returns Object with showAbove and showBelow booleans
 */
export function computeScrollIndicators(
  scrollOffset: number,
  maxVisible: number,
  totalItems: number,
): { showAbove: boolean; showBelow: boolean } {
  return {
    showAbove: scrollOffset > 0,
    showBelow: scrollOffset + maxVisible < totalItems,
  };
}

// --- MenuSystem ---

export class MenuSystem {
  private terminal: Terminal;
  private inputManager: InputManager;
  private quickStatsOverlay: QuickStatsOverlay;

  constructor(terminal: Terminal, inputManager: InputManager) {
    this.terminal = terminal;
    this.inputManager = inputManager;
    this.quickStatsOverlay = new QuickStatsOverlay(terminal, inputManager);
  }

  /**
   * Show a menu and wait for user selection.
   *
   * Returns a Promise that resolves with the selected option index (0-based),
   * or -1 if the user cancelled/exited.
   */
  show(items: MenuItem[], config: MenuConfig = {}): Promise<number> {
    const {
      title,
      titleColor = 'yellow',
      showExit = true,
      exitLabel = 'Back',
      onSelect,
      onCancel,
      enableQuickStats = false,
      quickStatsData,
      enableCharacterSheet = false,
      onCharacterSheet,
      clearBelow: clearBelowConfig,
      reserveBottomRows = 0,
      columns = 1,
      onBeforeRender,
      boxed = false,
      enhancedHighlight = false,
      onDirectionKey,
      onEnterOverride,
      onHotkey,
      onHighlight,
    } = config;

    // Mutable layout values — can be updated by onBeforeRender on resize
    let startRow = config.startRow ?? 3;
    let startCol = config.startCol ?? 1;
    let maxWidth = config.maxWidth;

    // Default clearBelow to true when startRow > 5 (after narrative content)
    const clearBelow = clearBelowConfig ?? startRow > 5;

    // Build list of selectable options (excluding separators and disabled)
    const selectableIndices: number[] = [];
    // Map from display number (1-based) to original item index
    const numberToOriginalIndex = new Map<number, number>();
    // Reverse map: original index → position in selectableIndices
    const originalToSelectable = new Map<number, number>();
    let displayNum = 0;

    items.forEach((item, i) => {
      if (!('type' in item)) {
        displayNum++;
        numberToOriginalIndex.set(displayNum, i);
        if (!item.disabled) {
          originalToSelectable.set(i, selectableIndices.length);
          selectableIndices.push(i);
        }
      }
    });

    const selectableCount = selectableIndices.length + (showExit ? 1 : 0);
    this.inputManager.setMenuItemCount(selectableCount);
    this.inputManager.setHighlightIndex(config.initialHighlightIndex ?? 0);
    this.inputManager.setMode('menu');

    // Map from terminal row to selectable positions with column ranges (for mouse clicks)
    const rowToSelectableIndex = new Map<number, Array<{ colStart: number; colEnd: number; selectablePos: number }>>();

    /** Look up the selectable position for a given row and column. */
    const lookupSelectable = (row: number, col?: number): number | undefined => {
      const entries = rowToSelectableIndex.get(row);
      if (!entries || entries.length === 0) return undefined;
      // Single entry (single-column mode or exit row) — return directly
      if (entries.length === 1) return entries[0].selectablePos;
      // Multi-column: find the entry whose column range contains the click
      if (col !== undefined) {
        for (const e of entries) {
          if (col >= e.colStart && col < e.colEnd) return e.selectablePos;
        }
      }
      // Fallback to first entry if no column match
      return entries[0].selectablePos;
    };

    // Track first render for clearBelow behavior
    let isFirstRender = true;

    // Flag set by resize handler — onBeforeRender only fires when this is true
    let needsFullRedraw = false;

    // Scroll offset for single-column mode when maxVisibleItems is set
    let scrollOffset = 0;
    const explicitMaxVisible = config.maxVisibleItems;

    /**
     * Auto-calculate max visible items based on available viewport rows.
     * Only applies to single-column mode. When the caller provides an explicit
     * maxVisibleItems, that value is used.
     *
     * Two-pass approach (RK-002 fix):
     *   Pass 1: Calculate space WITHOUT scroll indicators. If items fit → no pagination.
     *   Pass 2: If items overflow, recalculate WITH 2-row scroll indicator overhead.
     * This eliminates the off-by-one that hid the last 1-2 items when scrolling
     * wasn't actually needed.
     */
    const computeAutoMaxVisible = (): number | undefined => {
      // Multi-column menus don't use scroll — skip auto-calculation
      if (colCount > 1) return undefined;
      // If caller explicitly set maxVisibleItems, use it
      if (explicitMaxVisible !== undefined && explicitMaxVisible > 0) return explicitMaxVisible;
      // Only auto-paginate when there are enough items to potentially overflow
      if (items.length <= 5) return undefined;

      const { rows: termRows } = this.terminal.getSize();
      let baseOverhead = 0;
      // Title + separator
      if (title && !boxed) baseOverhead += 2;
      // Boxed top border (when boxed + title or no title)
      if (boxed) {
        baseOverhead += 1; // top border
        if (title) baseOverhead += 1; // title takes a line inside the box
      }
      // Exit section: separator + exit option + separator (non-boxed) or divider + exit + bottom border (boxed)
      if (showExit) baseOverhead += boxed ? 3 : 3;
      else if (boxed) baseOverhead += 1; // just the bottom border
      // Input prompt row
      baseOverhead += 1;
      // Bottom reserve
      baseOverhead += reserveBottomRows;

      // Pass 1: How many items fit WITHOUT scroll indicators?
      const availableWithout = termRows - startRow - baseOverhead + 1;
      if (items.length <= availableWithout) return undefined; // No pagination needed

      // Pass 2: Items overflow → budget 2 rows for scroll indicators
      const availableWith = availableWithout - 2;
      return Math.max(3, availableWith);
    };

    // Multi-column layout helpers (recalculated on resize via onBeforeRender)
    const colCount = Math.max(1, columns);
    let menuAreaWidth = maxWidth ?? this.terminal.getSize().cols;
    let colWidth = Math.floor((menuAreaWidth - startCol) / colCount);
    let frameAware = maxWidth !== undefined && maxWidth > 0 && startCol > 1;
    let clearWidth = maxWidth ?? 0;

    // Effective max visible items — recalculated on resize
    let effectiveMaxVisible = computeAutoMaxVisible();

    /** Clear a row's content area without destroying frame borders */
    const clearLine = (row: number) => {
      this.terminal.setCursor(row, startCol);
      if (frameAware) {
        this.terminal.write(' '.repeat(clearWidth));
        this.terminal.setCursor(row, startCol);
      } else {
        this.terminal.writeRaw('\x1b[2K');
      }
    };

    /** Draw a separator line within the content area */
    const drawSep = (row: number) => {
      if (frameAware) {
        this.terminal.setCursor(row, startCol);
        this.terminal.write(' '.repeat(clearWidth));
        this.terminal.setCursor(row, startCol);
        this.terminal.write('-'.repeat(clearWidth), 'khaki');
      } else {
        this.terminal.setCursor(row, startCol);
        this.terminal.writeRaw('\x1b[2K');
        this.terminal.separator('-', 'khaki');
      }
    };

    // Render function
    const render = () => {
      // Only call onBeforeRender when a resize actually happened (not on every highlight change)
      if (needsFullRedraw && onBeforeRender) {
        needsFullRedraw = false;
        const updated = onBeforeRender();
        if (updated) {
          startRow = updated.startRow;
          startCol = updated.startCol;
          maxWidth = updated.maxWidth;
          menuAreaWidth = maxWidth ?? this.terminal.getSize().cols;
          colWidth = Math.floor((menuAreaWidth - startCol) / colCount);
          frameAware = maxWidth !== undefined && maxWidth > 0 && startCol > 1;
          clearWidth = maxWidth ?? 0;
        }
        // Recompute auto-pagination after layout change
        effectiveMaxVisible = computeAutoMaxVisible();
      }

      const highlightIdx = this.inputManager.getHighlightIndex();
      let currentRow = startRow;
      let displayNumber = 0;

      // Boxed mode variables (hoisted for use in both items and exit sections)
      const boxWidth = frameAware ? clearWidth : Math.min(menuAreaWidth - startCol + 1, 56);
      const bc = theme.fg('darkAmber' as ColorName);
      const ANSI_RESET = '\x1b[0m';

      // Title (skip if boxed — title is rendered in box top border)
      if (title && !boxed) {
        clearLine(currentRow);
        this.terminal.write(`  ${title}`, titleColor);
        currentRow++;
        drawSep(currentRow);
        currentRow++;
      }

      // Clear the row-to-index mapping
      rowToSelectableIndex.clear();

      // Menu items — single column vs multi-column
      if (colCount <= 1) {
        // Single-column mode with optional scroll support
        const needsScroll = effectiveMaxVisible !== undefined && effectiveMaxVisible > 0 && items.length > effectiveMaxVisible;
        const maxVisible = needsScroll ? Math.max(1, effectiveMaxVisible!) : items.length;

        // Recalculate scroll offset so the highlighted item is visible.
        // Map selectable highlight index to position in the full items array.
        if (needsScroll) {
          const highlightedItemIdx = highlightIdx < selectableIndices.length
            ? selectableIndices[highlightIdx]
            : items.length - 1; // fallback for exit highlight
          scrollOffset = computeScrollOffset(highlightedItemIdx, scrollOffset, maxVisible);
          // Clamp scrollOffset so we don't go past the end
          scrollOffset = Math.min(scrollOffset, Math.max(0, items.length - maxVisible));
        } else {
          scrollOffset = 0;
        }

        // Compute scroll indicators
        const { showAbove, showBelow } = needsScroll
          ? computeScrollIndicators(scrollOffset, maxVisible, items.length)
          : { showAbove: false, showBelow: false };

        // Box width for boxed mode
        // (bc, boxWidth, ANSI_RESET hoisted above for use in exit section too)

        // Boxed mode: top border
        if (boxed) {
          clearLine(currentRow);
          this.terminal.setCursor(currentRow, startCol);
          if (title) {
            const titleText = ` ${title} `;
            const fill = Math.max(0, boxWidth - 2 - titleText.length);
            this.terminal.writeRaw(`${bc}┌─${theme.fg('amber' as ColorName)}${titleText}${bc}${'─'.repeat(fill)}┐${ANSI_RESET}`);
          } else {
            this.terminal.writeRaw(`${bc}┌${'─'.repeat(boxWidth - 2)}┐${ANSI_RESET}`);
          }
          currentRow++;
        }

        // Show "▲ more" indicator if items overflow above
        if (showAbove) {
          clearLine(currentRow);
          if (boxed) {
            this.terminal.setCursor(currentRow, startCol);
            this.terminal.writeRaw(`${bc}│${ANSI_RESET}`);
            this.terminal.write('   ▲ more', 'khaki');
            this.terminal.setCursor(currentRow, startCol + boxWidth - 1);
            this.terminal.writeRaw(`${bc}│${ANSI_RESET}`);
          } else {
            this.terminal.write('    ▲ more', 'khaki');
          }
          // Register click target for scroll-up (sentinel: -10)
          rowToSelectableIndex.set(currentRow, [{ colStart: 0, colEnd: 9999, selectablePos: -10 }]);
          currentRow++;
        }

        // Determine the visible slice of items
        const visibleStart = scrollOffset;
        const visibleEnd = Math.min(scrollOffset + maxVisible, items.length);

        // We need to track displayNumber across ALL items (not just visible ones)
        // so that the visible items show the correct numbering.
        // Pre-compute displayNumber for items before the visible window.
        for (let i = 0; i < visibleStart; i++) {
          if (!('type' in items[i])) {
            displayNumber++;
          }
        }

        // Render only the visible slice
        for (let i = visibleStart; i < visibleEnd; i++) {
          const item = items[i];
          const originalIndex = i;
          clearLine(currentRow);

          if ('type' in item && item.type === 'separator') {
            if (item.label) {
              const sepWidth = frameAware ? clearWidth : this.terminal.getSize().cols;
              const pad = Math.max(0, Math.floor((sepWidth - item.label.length - 4) / 2));
              const line = '-'.repeat(pad) + `< ${item.label} >` + '-'.repeat(pad);
              this.terminal.write(`  ${line}`, 'khaki');
            } else {
              if (frameAware) {
                this.terminal.write('-'.repeat(clearWidth), 'khaki');
              } else {
                this.terminal.separator('-', 'khaki');
              }
            }
          } else {
            displayNumber++;
            const opt = item as MenuOption;
            const selectablePos = originalToSelectable.get(originalIndex);
            const isHighlighted = selectablePos !== undefined && selectablePos === highlightIdx;
            const number = displayNumber;
            const color = opt.disabled ? 'charcoal' : (opt.color ?? 'white');

            // Consistent 2-space left margin
            const leftMargin = '  ';
            const prefix = isHighlighted ? ' > ' : '   ';
            // Right-align option numbers within consistent width (2 chars for numbers up to 99)
            const numWidth = 2;
            const numStr = `${number.toString().padStart(numWidth)}) `;

            let suffix = '';
            let suffixColor: ColorName = opt.suffixColor ?? 'khaki';

            if (opt.skillCheck) {
              const peekResult = formatStatPeek({
                statAbbrev: opt.skillCheck.stat,
                statValue: opt.skillCheck.playerValue,
                threshold: opt.skillCheck.dc,
              });
              suffix = ` ${peekResult.text}`;
              suffixColor = peekResult.color;
            } else if (opt.suffix) {
              suffix = ` ${opt.suffix}`;
            }

            if (opt.disabled) {
              const disabledColor: ColorName = 'charcoal';
              if (boxed) {
                const text = `${leftMargin}${prefix}${numStr}${opt.label}${suffix}`;
                const pad = Math.max(0, boxWidth - 2 - text.length);
                this.terminal.setCursor(currentRow, startCol);
                this.terminal.writeRaw(`${bc}│${ANSI_RESET}`);
                this.terminal.write(text, disabledColor);
                this.terminal.write(' '.repeat(pad));
                this.terminal.setCursor(currentRow, startCol + boxWidth - 1);
                this.terminal.writeRaw(`${bc}│${ANSI_RESET}`);
              } else {
                this.terminal.write(`${leftMargin}${prefix}${numStr}${opt.label}${suffix}`, disabledColor);
              }
            } else if (isHighlighted) {
              if (enhancedHighlight) {
                // Enhanced: amber > prefix, cream text
                const text = `${leftMargin}${prefix}${numStr}${opt.label}${suffix}`;
                if (boxed) {
                  const pad = Math.max(0, boxWidth - 2 - text.length);
                  this.terminal.setCursor(currentRow, startCol);
                  this.terminal.writeRaw(`${bc}│${ANSI_RESET}`);
                  this.terminal.write(`${leftMargin}`, 'white');
                  this.terminal.write('> ', 'amber');
                  this.terminal.write(`${numStr}${opt.label}`, 'cream');
                  if (suffix) this.terminal.write(suffix, suffixColor);
                  this.terminal.write(' '.repeat(Math.max(0, pad - (suffix ? 0 : 0))));
                  this.terminal.setCursor(currentRow, startCol + boxWidth - 1);
                  this.terminal.writeRaw(`${bc}│${ANSI_RESET}`);
                } else {
                  this.terminal.write(`${leftMargin}`, 'white');
                  this.terminal.write('> ', 'amber');
                  this.terminal.write(`${numStr}${opt.label}`, 'cream');
                  if (suffix) this.terminal.write(suffix, suffixColor);
                }
              } else {
                if (boxed) {
                  const text = `${leftMargin}${prefix}${numStr}${opt.label}${suffix}`;
                  const pad = Math.max(0, boxWidth - 2 - text.length);
                  this.terminal.setCursor(currentRow, startCol);
                  this.terminal.writeRaw(`${bc}│${ANSI_RESET}`);
                  this.terminal.writeRaw(theme.highlight(`${text}${' '.repeat(pad)}`));
                  this.terminal.setCursor(currentRow, startCol + boxWidth - 1);
                  this.terminal.writeRaw(`${bc}│${ANSI_RESET}`);
                } else {
                  this.terminal.write(leftMargin);
                  this.terminal.writeRaw(
                    theme.highlight(`${prefix}${numStr}${opt.label}${suffix}`)
                  );
                }
              }
            } else {
              const itemColor: ColorName = enhancedHighlight ? 'sepia' : color;
              if (boxed) {
                this.terminal.setCursor(currentRow, startCol);
                this.terminal.writeRaw(`${bc}│${ANSI_RESET}`);
                this.terminal.write(`${leftMargin}${prefix}${numStr}`, enhancedHighlight ? 'sepia' : 'white');
                this.terminal.write(opt.label, itemColor);
                if (suffix) this.terminal.write(suffix, suffixColor);
                // Pad to box width
                const textLen = leftMargin.length + prefix.length + numStr.length + opt.label.length + suffix.length;
                const pad = Math.max(0, boxWidth - 2 - textLen);
                this.terminal.write(' '.repeat(pad));
                this.terminal.setCursor(currentRow, startCol + boxWidth - 1);
                this.terminal.writeRaw(`${bc}│${ANSI_RESET}`);
              } else {
                this.terminal.write(`${leftMargin}${prefix}${numStr}`, enhancedHighlight ? 'sepia' : 'white');
                this.terminal.write(opt.label, itemColor);
                if (suffix) {
                  this.terminal.write(suffix, suffixColor);
                }
              }
            }

            if (selectablePos !== undefined) {
              rowToSelectableIndex.set(currentRow, [{ colStart: 0, colEnd: 9999, selectablePos }]);
            }

            // Render description on the next line when item is highlighted
            if (isHighlighted && opt.description) {
              currentRow++;
              clearLine(currentRow);
              const descIndent = `${leftMargin}     `;
              if (boxed) {
                const descText = `${descIndent}${opt.description}`;
                const pad = Math.max(0, boxWidth - 2 - descText.length);
                this.terminal.setCursor(currentRow, startCol);
                this.terminal.writeRaw(`${bc}│${ANSI_RESET}`);
                this.terminal.write(descText, 'khaki');
                this.terminal.write(' '.repeat(pad));
                this.terminal.setCursor(currentRow, startCol + boxWidth - 1);
                this.terminal.writeRaw(`${bc}│${ANSI_RESET}`);
              } else {
                this.terminal.write(`${descIndent}${opt.description}`, 'khaki');
              }
            }
          }
          currentRow++;
        }

        // Show "▼ more" indicator if items overflow below
        if (showBelow) {
          clearLine(currentRow);
          if (boxed) {
            this.terminal.setCursor(currentRow, startCol);
            this.terminal.writeRaw(`${bc}│${ANSI_RESET}`);
            this.terminal.write('   ▼ more', 'khaki');
            this.terminal.setCursor(currentRow, startCol + boxWidth - 1);
            this.terminal.writeRaw(`${bc}│${ANSI_RESET}`);
          } else {
            this.terminal.write('    ▼ more', 'khaki');
          }
          // Register click target for scroll-down (sentinel: -11)
          rowToSelectableIndex.set(currentRow, [{ colStart: 0, colEnd: 9999, selectablePos: -11 }]);
          currentRow++;
        }
      } else {
        // Multi-column mode — distribute items across columns
        const optionItems: Array<{ item: MenuOption; originalIndex: number }> = [];
        items.forEach((item, originalIndex) => {
          if (!('type' in item)) {
            optionItems.push({ item: item as MenuOption, originalIndex });
          }
        });

        const rowsNeeded = Math.ceil(optionItems.length / colCount);

        for (let r = 0; r < rowsNeeded; r++) {
          clearLine(currentRow);

          for (let c = 0; c < colCount; c++) {
            const idx = c * rowsNeeded + r;
            if (idx >= optionItems.length) continue;

            const { item: opt, originalIndex } = optionItems[idx];
            const num = idx + 1;
            const col = startCol + c * colWidth;
            const selectablePos = originalToSelectable.get(originalIndex);
            const isHighlighted = selectablePos !== undefined && selectablePos === highlightIdx;
            const color = opt.disabled ? 'charcoal' : (opt.color ?? 'white');

            const prefix = isHighlighted ? '> ' : '  ';
            const numStr = `${num}) `;
            let suffix = '';
            if (opt.suffix) suffix = ` ${opt.suffix}`;

            this.terminal.setCursor(currentRow, col);
            if (opt.disabled) {
              this.terminal.write(`${prefix}${numStr}${opt.label}`, 'charcoal');
            } else if (isHighlighted) {
              this.terminal.writeRaw(theme.highlight(`${prefix}${numStr}${opt.label}${suffix}`));
            } else {
              this.terminal.write(`${prefix}${numStr}`, 'white');
              this.terminal.write(opt.label, color);
              if (suffix) this.terminal.write(suffix, opt.suffixColor ?? 'khaki');
            }

            if (selectablePos !== undefined) {
              const colStart = col;
              const colEnd = col + colWidth;
              if (!rowToSelectableIndex.has(currentRow)) {
                rowToSelectableIndex.set(currentRow, []);
              }
              rowToSelectableIndex.get(currentRow)!.push({ colStart, colEnd, selectablePos });
            }
          }
          currentRow++;
        }
      }

      // Separator before exit
      if (showExit) {
        if (boxed) {
          // Boxed: draw internal divider
          clearLine(currentRow);
          this.terminal.setCursor(currentRow, startCol);
          this.terminal.writeRaw(`${bc}├${'─'.repeat(boxWidth - 2)}┤${ANSI_RESET}`);
          currentRow++;

          const isExitHighlighted = (selectableCount - 1) === highlightIdx;
          clearLine(currentRow);
          const leftMargin = '  ';
          const exitPrefix = isExitHighlighted ? '> ' : '  ';
          const exitNumStr = ' 0) ';
          const exitText = `${leftMargin}${exitPrefix}${exitNumStr}${exitLabel}`;
          const exitPad = Math.max(0, boxWidth - 2 - exitText.length);
          this.terminal.setCursor(currentRow, startCol);
          this.terminal.writeRaw(`${bc}│${ANSI_RESET}`);
          if (isExitHighlighted) {
            if (enhancedHighlight) {
              this.terminal.write(`${leftMargin}`, 'white');
              this.terminal.write('> ', 'amber');
              this.terminal.write(`${exitNumStr}${exitLabel}`, 'cream');
              this.terminal.write(' '.repeat(exitPad));
            } else {
              this.terminal.writeRaw(theme.highlight(`${exitText}${' '.repeat(exitPad)}`));
            }
          } else {
            this.terminal.write(`${exitText}`, 'warmRed');
            this.terminal.write(' '.repeat(exitPad));
          }
          this.terminal.setCursor(currentRow, startCol + boxWidth - 1);
          this.terminal.writeRaw(`${bc}│${ANSI_RESET}`);
          rowToSelectableIndex.set(currentRow, [{ colStart: 0, colEnd: 9999, selectablePos: selectableCount - 1 }]);
          currentRow++;

          // Bottom border of box
          clearLine(currentRow);
          this.terminal.setCursor(currentRow, startCol);
          this.terminal.writeRaw(`${bc}└${'─'.repeat(boxWidth - 2)}┘${ANSI_RESET}`);
          currentRow++;
        } else {
          drawSep(currentRow);
          currentRow++;

          const isExitHighlighted = (selectableCount - 1) === highlightIdx;
          clearLine(currentRow);
          const leftMargin = '  ';
          const exitPrefix = isExitHighlighted ? '> ' : '  ';
          const exitNumStr = ' 0) '; // Padded to match 2-char width
          if (isExitHighlighted) {
            if (enhancedHighlight) {
              this.terminal.write(`${leftMargin}`, 'white');
              this.terminal.write('> ', 'amber');
              this.terminal.write(`${exitNumStr}${exitLabel}`, 'cream');
            } else {
              this.terminal.write(leftMargin);
              this.terminal.writeRaw(theme.highlight(`${exitPrefix}${exitNumStr}${exitLabel}`));
            }
          } else {
            this.terminal.write(`${leftMargin}${exitPrefix}${exitNumStr}${exitLabel}`, 'warmRed');
          }
          rowToSelectableIndex.set(currentRow, [{ colStart: 0, colEnd: 9999, selectablePos: selectableCount - 1 }]);
          currentRow++;

          drawSep(currentRow);
          currentRow++;
        }
      } else if (boxed) {
        // No exit but boxed — close the box
        clearLine(currentRow);
        this.terminal.setCursor(currentRow, startCol);
        this.terminal.writeRaw(`${bc}└${'─'.repeat(boxWidth - 2)}┘${ANSI_RESET}`);
        currentRow++;
      }

      // Input prompt
      clearLine(currentRow);
      this.terminal.write('  > ', 'yellow');
      this.terminal.showCursor();

      // Safety clamp (RK-002): if the prompt row exceeds available space,
      // force scroll mode on next render to prevent overflow regression
      const { rows: termRowsCheck } = this.terminal.getSize();
      const maxAllowedRow = termRowsCheck - reserveBottomRows;
      if (currentRow > maxAllowedRow && effectiveMaxVisible === undefined) {
        effectiveMaxVisible = computeAutoMaxVisible() ?? Math.max(3, items.length - 2);
      }

      // Clear rows below menu on first render to prevent narrative bleed-through
      if (clearBelow && isFirstRender) {
        const { rows } = this.terminal.getSize();
        // When frame-aware, stop 1 row before the bottom border (which is at rows)
        const defaultEnd = frameAware ? rows - 1 : rows;
        const clearEnd = reserveBottomRows > 0 ? rows - reserveBottomRows : defaultEnd;
        if (frameAware) {
          // Clear only the content area, preserve side borders and bottom border
          this.terminal.clearRegion(currentRow + 1, clearEnd, startCol, startCol + clearWidth - 1);
        } else {
          this.terminal.clearFromRow(currentRow + 1, clearEnd);
        }
        isFirstRender = false;
      }
    };

    // Initial render
    render();

    // Call onHighlight for initial highlight position
    onHighlight?.(this.inputManager.getHighlightIndex());

    // Re-render on terminal resize so the menu stays correct
    const resizeHandler = () => { needsFullRedraw = true; render(); };
    this.terminal.onResize(resizeHandler);

    return new Promise<number>((resolve) => {
      const handler = async (event: InputEvent) => {
        switch (event.type) {
          case 'highlightMove': {
            // Intercept directional keys for external handlers (e.g., map navigation)
            if (event.direction && onDirectionKey) {
              const dir = event.direction;
              if (dir === 'left' || dir === 'right' || dir === 'up' || dir === 'down') {
                if (onDirectionKey(dir)) break; // consumed by external handler
              }
            }
            if (event.mouseRow !== undefined) {
              const idx = lookupSelectable(event.mouseRow, event.mouseCol);
              if (idx !== undefined && idx >= 0) {
                this.inputManager.setHighlightIndex(idx);
              }
              // Ignore sentinel values (-10, -11) for highlight — handled on click
            }
            render();
            // Call onHighlight callback after render with current highlight index
            onHighlight?.(this.inputManager.getHighlightIndex());
            break;
          }

          case 'hotkey': {
            // Let external handler intercept first
            if (onHotkey && event.key && onHotkey(event.key)) {
              render(); // Re-render in case focus state changed
              break;
            }
            // Handle Tab or ? for quick stats overlay
            if (enableQuickStats && quickStatsData && (event.key === 'Tab' || event.key === '?')) {
              await this.quickStatsOverlay.show(quickStatsData);
              render(); // Re-render menu after overlay closes
            }
            // Handle 'c' or 'C' for character sheet
            if (enableCharacterSheet && onCharacterSheet && (event.key === 'c' || event.key === 'C')) {
              await onCharacterSheet();
              render(); // Re-render menu after character sheet closes
            }
            break;
          }

          case 'menuSelect': {
            // Check if external handler wants to override Enter key
            if (onEnterOverride && event.number === undefined && event.mouseRow === undefined) {
              const overrideResult = onEnterOverride();
              if (overrideResult !== -2) {
                // External handler consumed the Enter key
                if (overrideResult === -1) {
                  this.cleanup(handler, resizeHandler);
                  onCancel?.();
                  resolve(-1);
                  return;
                }
                if (overrideResult >= 0 && overrideResult < items.length) {
                  this.cleanup(handler, resizeHandler);
                  onSelect?.(overrideResult);
                  resolve(overrideResult);
                  return;
                }
              }
            }

            let selectedIndex: number;

            if (event.mouseRow !== undefined) {
              const idx = lookupSelectable(event.mouseRow, event.mouseCol);
              if (idx === undefined) return;
              // Handle "▲ more" / "▼ more" click sentinels
              if (idx === -10) {
                // Scroll up
                const scrollAmount = Math.max(1, Math.floor((effectiveMaxVisible ?? 5) / 2));
                const curIdx = this.inputManager.getHighlightIndex();
                const newIdx = Math.max(0, curIdx - scrollAmount);
                this.inputManager.setHighlightIndex(newIdx);
                render();
                onHighlight?.(newIdx);
                return;
              }
              if (idx === -11) {
                // Scroll down
                const scrollAmount = Math.max(1, Math.floor((effectiveMaxVisible ?? 5) / 2));
                const curIdx = this.inputManager.getHighlightIndex();
                const newIdx = Math.min(selectableCount - 1, curIdx + scrollAmount);
                this.inputManager.setHighlightIndex(newIdx);
                render();
                onHighlight?.(newIdx);
                return;
              }
              selectedIndex = idx;
            } else if (event.number !== undefined) {
              if (event.number === 0) {
                this.cleanup(handler, resizeHandler);
                onCancel?.();
                resolve(-1);
                return;
              }
              // Map display number to original index, then to selectable position
              const origIdx = numberToOriginalIndex.get(event.number);
              if (origIdx === undefined) return;
              const selPos = originalToSelectable.get(origIdx);
              if (selPos === undefined) return; // disabled item, ignore
              selectedIndex = selPos;
            } else if (event.index !== undefined) {
              selectedIndex = event.index;
            } else {
              return;
            }

            if (showExit && selectedIndex === selectableCount - 1) {
              this.cleanup(handler, resizeHandler);
              onCancel?.();
              resolve(-1);
              return;
            }

            if (selectedIndex >= 0 && selectedIndex < selectableIndices.length) {
              this.cleanup(handler, resizeHandler);
              const resolvedIndex = selectableIndices[selectedIndex];
              onSelect?.(resolvedIndex);
              resolve(resolvedIndex);
            }
            break;
          }

          case 'cancel': {
            this.cleanup(handler, resizeHandler);
            onCancel?.();
            resolve(-1);
            break;
          }

          case 'scrollUp': {
            if (effectiveMaxVisible && effectiveMaxVisible > 0) {
              // PageUp / scroll wheel: move by half a page for faster navigation
              const pageStep = Math.max(1, Math.floor(effectiveMaxVisible / 2));
              const curIdx = this.inputManager.getHighlightIndex();
              const newIdx = Math.max(0, curIdx - pageStep);
              this.inputManager.setHighlightIndex(newIdx);
              render();
            }
            break;
          }
          case 'scrollDown': {
            if (effectiveMaxVisible && effectiveMaxVisible > 0) {
              // PageDown / scroll wheel: move by half a page for faster navigation
              const pageStep = Math.max(1, Math.floor(effectiveMaxVisible / 2));
              const curIdx = this.inputManager.getHighlightIndex();
              const newIdx = Math.min(selectableCount - 1, curIdx + pageStep);
              this.inputManager.setHighlightIndex(newIdx);
              render();
            }
            break;
          }
        }
      };

      this.inputManager.on(handler);
    });
  }

  /** Clean up event handlers after menu closes */
  private cleanup(handler: (event: InputEvent) => void, resizeHandler?: () => void): void {
    this.inputManager.off(handler);
    this.inputManager.drain(); // Prevent keystroke cascade (RK-073)
    this.terminal.hideCursor();
    if (resizeHandler) {
      this.terminal.offResize(resizeHandler);
    }
  }
}
